import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import { FiArrowRight, FiClipboard, FiClock } from 'react-icons/fi';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '@ohif/i18n';

import Menu from './Menu';
import { usePermissions } from '../helpers/acl/permissions';
import whitelogo from '../deepecho_icon_dark.png';
import largeLogo from '../deepecho_icon.png';
import smallLogo from '../small_deepecho_icon.png';
import whitesmalllogo from '../small_deepecho_icon_dark.png';
import './layout.css';
import MobileMenu from './MobileMenu';
import { Modal } from '../UI/ComponnentsUI/common/Modal';
import { useLogout } from '../helpers/api/auth/auth';
import {
  clearAllSurveyReminderCaches,
  localizeSurveyText,
  useSurveyReminder,
} from '../helpers/api/surveys/surveys';
import {
  getPreferenceLanguageFromApi,
  preferenceLanguageToI18n,
} from '../helpers/i18n/languagePreference';
import { usePlatformMode } from '../hooks/usePlatformMode';
import NotificationInbox from './NotificationInbox';
import { useMe, usePreferenceUser } from '../helpers/api/users/users';

interface LayoutProps {
  children: ReactNode;
}

interface MenuItem {
  title: string;
  link: string;
  icon: string;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { t } = useTranslation('Menu');
  const { t: tCommon } = useTranslation('Common');
  const { t: tSurveys } = useTranslation('Surveys');
  const [menuOpen, setMenuOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isSurveyPromptOpen, setIsSurveyPromptOpen] = useState(false);
  const [hasHandledSurveyPrompt, setHasHandledSurveyPrompt] = useState(false);
  const user = useMe({ retry: 0 });
  const navigate = useNavigate();
  const location = useLocation();
  const logoutMutation = useLogout();
  const { data: preferencesData } = usePreferenceUser();
  const { data: surveyReminder } = useSurveyReminder(user?.data?.id);
  const { mode: platformMode, loading: platformLoading } = usePlatformMode();

  useEffect(() => {
    const prefs = preferencesData?.data;
    if (!prefs) {
      return;
    }

    const theme = prefs.theme === 'dark' ? 'deepechodark' : 'deepecho';
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);

    const normalizedLang = preferenceLanguageToI18n(
      getPreferenceLanguageFromApi(preferencesData)
    );
    if (i18n.language !== normalizedLang) {
      void i18n.changeLanguage(normalizedLang);
      localStorage.setItem('i18nextLng', normalizedLang);
    }
  }, [preferencesData]);

  useEffect(() => {
    if (location.pathname === '/archi') {
      setMenuOpen(false);
    }
  }, [location.pathname]);

  const isOnSurveyRoute = location.pathname.startsWith('/surveys');
  const primaryPendingSurvey = surveyReminder?.primary_survey ?? null;
  const dueSurveyCount = surveyReminder?.due_count ?? 0;
  const hasDueSurveys = Boolean(surveyReminder?.has_due_surveys);
  const shouldShowSurveyBanner = hasDueSurveys && hasHandledSurveyPrompt && !isOnSurveyRoute;

  useEffect(() => {
    if (!hasDueSurveys) {
      setIsSurveyPromptOpen(false);
      setHasHandledSurveyPrompt(false);
      return;
    }

    if (isOnSurveyRoute) {
      setIsSurveyPromptOpen(false);
      setHasHandledSurveyPrompt(true);
      return;
    }

    if (hasHandledSurveyPrompt) {
      return;
    }

    setIsSurveyPromptOpen(true);
  }, [hasDueSurveys, isOnSurveyRoute, hasHandledSurveyPrompt]);

  const go_profile = (uid: string) => {
    navigate(`/profile/${uid}`);
  };

  const handleLogout = async () => {
    try {
      clearAllSurveyReminderCaches();
      await logoutMutation.mutateAsync({});
      window.location.href = '/login';
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  // Nav gating by the user's effective permission set. OR semantics; [] = shown to
  // any signed-in user. Backend enforces regardless.
  const { perms, can } = usePermissions();
  const isAdmin = can(['user.read', 'org_unit.read', 'role.read']);
  // Surveys are about clinical use, so only clinical roles (those who acquire/read studies)
  // are prompted. This also keeps the survey prompt from colliding with the org-admin-only
  // billing nudge, which targets a different audience.
  const isClinicalUser = can(['study.acquire', 'study.read']);

  const mainMenuItems: MenuItem[] = useMemo(
    () => {
      const items: MenuItem[] = [];

      // Show loading state or default items while platform mode is loading
      if (platformLoading) {
        items.push({ title: t('Dashboard'), link: '/home', icon: 'ri-dashboard-line' });
        return items;
      }

    if (platformMode === 'api_as_service') {
      // API-as-service is available to any signed-in user.
      items.push({ title: t('API as Service'), link: '/api-as-service', icon: 'ri-code-s-slash-line' });
      return items;
    }

    const fullModeItems = [
      {
        title: t('Dashboard'),
        link: '/home',
        icon: 'ri-dashboard-line',
        permissions: [],
      },
      {
        title: t('PACS Inbox'),
        link: '/pacs-inbox',
        icon: 'ri-inbox-line',
        permissions: ['pacs_device.read'],
      },
      {
        title: t('Patients'),
        link: '/patients',
        icon: 'ri-group-line',
        permissions: ['patient.read'],
      },
      {
        title: t('Exams'),
        link: '/exams',
        icon: 'ri-file-list-3-line',
        permissions: ['study.read'],
      },
      {
        title: t('Scheduling'),
        link: '/Scheduling',
        icon: 'ri-calendar-event-line',
        permissions: ['appointment.read'],
      },
      {
        title: t('Realtime Exam'),
        link: '/realtimeexam',
        icon: 'ri-pulse-line',
        permissions: ['study.acquire'],
      },
      // {
      //   title: t('Worklist'),
      //   link: '/worklist',
      //   icon: 'ri-calendar-check-line',
      //   permissions: ['appointment.read'],
      // },
      {
        title: t('API as Service'),
        link: '/api-as-service',
        icon: 'ri-code-s-slash-line',
        permissions: [],
      },
      {
        title: t('Playground'),
        link: '/playground',
        icon: 'ri-flashlight-line',
        roles: ['admin', 'doctor', 'sonographer'],
      },
      {
        title: t('Organization'),
        link: '/organization',
        icon: 'ri-building-4-line',
        permissions: ['user.read', 'org_unit.read', 'role.read', 'pacs_device.manage'],
      },
      {
        title: t('Audit Trail'),
        link: '/audit-trail',
        icon: 'ri-shield-check-line',
        permissions: ['audit_log.read'],
      },
    ];

    fullModeItems
      .filter((item) => can(item.permissions))
      .forEach(({ permissions: _permissions, ...item }) => items.push(item));

    return items;
  }, [t, platformMode, platformLoading, perms]);

  const capitalizeEachWord = (name: string): string => {
    if (!name || name.trim() === '') return tCommon('defaultUserName');
    return name
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const userName = capitalizeEachWord(
    `${user?.data?.first_name || ''} ${user?.data?.last_name || ''}`.trim()
  );

  const userInitials = ((user?.data?.first_name?.[0] || '') + (user?.data?.last_name?.[0] || 'A')).toUpperCase();

  const handleSurveyPromptDismiss = () => {
    setIsSurveyPromptOpen(false);
    setHasHandledSurveyPrompt(true);
  };

  const handleSurveyPromptOpen = () => {
    setIsSurveyPromptOpen(false);
    setHasHandledSurveyPrompt(true);

    if (dueSurveyCount === 1 && primaryPendingSurvey) {
      navigate(`/surveys/${primaryPendingSurvey.id}`);
      return;
    }

    navigate('/surveys');
  };

  return (
    <div className="flex h-screen w-full gap-4 bg-card-color-200 dark:bg-gray-900">
      <Menu
        mainMenuItems={mainMenuItems}
        menuOpen={menuOpen}
        handleLogout={handleLogout}
        toggleMenu={() => setMenuOpen(!menuOpen)}
        logo={largeLogo}
        whiteLogo={whitelogo}
        smallLogo={smallLogo}
        whiteSmallLogo={whitesmalllogo}
        username={userName}
        userInitials={userInitials}
        go_profile={() => user?.data?.id && go_profile(user.data.id)}
        userRole={user?.data?.role}
        isAdmin={isAdmin}
      />

      <MobileMenu
        mainMenuItems={mainMenuItems}
        isOpen={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        handleLogout={handleLogout}
        username={userName}
        userInitials={userInitials}
        go_profile={() => user?.data?.id && go_profile(user.data.id)}
        userRole={user?.data?.role}
        isAdmin={isAdmin}
      />

      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <main className="flex min-h-0 flex-1 flex-col overflow-y-auto rounded-2xl bg-card-color-200 dark:bg-gray-900">
          <header className="flex shrink-0 items-center justify-between gap-3 border-b border-gray-200 px-4 py-3 dark:border-gray-700 md:hidden">
            <button
              type="button"
              onClick={() => setMobileMenuOpen(true)}
              className="rounded-lg p-2 text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
              aria-label="Open menu"
            >
              <i className="ri-menu-line text-2xl" />
            </button>
            <img src={largeLogo} alt="Deep Echo" className="h-5 dark:hidden" />
            <img src={whitelogo} alt="Deep Echo" className="hidden h-5 dark:block" />
            <NotificationInbox variant="compact" />
          </header>
          {shouldShowSurveyBanner && isClinicalUser ? (
            <div className="px-4 pt-4 sm:px-6">
              <button
                type="button"
                onClick={() => navigate('/surveys')}
                className="flex w-full items-center justify-between rounded-2xl border border-blue-200 bg-blue-50 px-4 py-3 text-left text-sm text-blue-900 transition hover:border-blue-300 hover:bg-blue-100 dark:border-blue-900 dark:bg-blue-950/60 dark:text-blue-100"
              >
                <span>{tSurveys('bannerTitle', { count: dueSurveyCount })}</span>
                <span className="font-semibold">{tSurveys('bannerAction')}</span>
              </button>
            </div>
          ) : null}
          <div className="flex h-full min-h-0 flex-1 flex-col">{children}</div>
        </main>
      </div>

      <Modal
        isOpen={isSurveyPromptOpen && isClinicalUser}
        onClose={handleSurveyPromptDismiss}
        size="lg"
      >
        <Modal.Header
          title={tSurveys('modalTitle')}
          description={tSurveys('modalDescription', { count: dueSurveyCount })}
          onClose={handleSurveyPromptDismiss}
        />
        <Modal.Body className="space-y-5">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-blue-600 dark:text-blue-300">
            <FiClipboard size={14} />
            <span>{tSurveys('modalEyebrow')}</span>
          </div>

          {primaryPendingSurvey ? (
            <div className="rounded-3xl border border-blue-100 bg-blue-50/80 p-5 dark:border-blue-900 dark:bg-blue-950/40">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-blue-700 dark:text-blue-200">
                <FiClock size={13} />
                <span>{tSurveys('modalDueNow')}</span>
              </div>
              <h3 className="mt-3 text-xl font-semibold text-gray-900 dark:text-white">
                {localizeSurveyText(primaryPendingSurvey.title, i18n.language)}
              </h3>
              <p className="mt-2 text-sm leading-6 text-gray-600 dark:text-gray-300">
                {localizeSurveyText(primaryPendingSurvey.description, i18n.language)}
              </p>
              <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
                {dueSurveyCount > 1
                  ? tSurveys('modalFollowUpMultiple', { count: dueSurveyCount - 1 })
                  : tSurveys('modalFollowUpSingle')}
              </p>
            </div>
          ) : null}

          <div className="rounded-2xl bg-slate-50 px-4 py-3 text-sm text-slate-600 dark:bg-gray-900/60 dark:text-slate-300">
            {tSurveys('modalTimeHint')}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            onClick={handleSurveyPromptDismiss}
            className="rounded-full border border-gray-300 px-5 py-2.5 text-sm font-semibold text-gray-700 transition hover:border-gray-400 hover:text-gray-900 dark:border-gray-600 dark:text-gray-200 dark:hover:border-gray-500"
          >
            {tSurveys('modalLater')}
          </button>
          <button
            type="button"
            onClick={handleSurveyPromptOpen}
            className="inline-flex items-center gap-2 rounded-full bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-700"
          >
            {tSurveys('modalPrimary')}
            <FiArrowRight size={14} />
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Layout;
